import { gql } from '@apollo/client';
import { useLazyQuery } from '@apollo/client/react';
import { useEffect, useRef } from 'react';

import {
  VirusScanStatus,
  type GetAttachmentMetadataQuery,
} from 'src/__generated__/graphql';
import type { FileAttachment } from 'src/forms/common/AttachmentSelect/AttachmentSelect';
import { asyncPoll } from 'src/utils';

const MAX_ATTEMPTS = 8;
const INTERVAL_MS = 1000;

// TODO: type is not genereated for some reason
export const GET_ATTACHMENT_METADATA = gql(`
  query GetAttachmentMetadata($blobUrl: String!) {
    viewer {
      _id
      attachmentMetadata(blobUrl: $blobUrl) {
        fileName
        virusScanStatus
      }
    }
  }
`);

interface VirusScanArgs {
  fileUri?: string;
  currentMethod?: FileAttachment;
  onStatusChange?: (status: VirusScanStatus, method?: FileAttachment) => void;
}

export function useVirusScan({
  fileUri,
  onStatusChange,
  currentMethod,
}: VirusScanArgs) {
  const count = useRef(MAX_ATTEMPTS);
  const onStatusChangeRef = useRef(onStatusChange);
  const [fetchFileMetadata, state] = useLazyQuery<GetAttachmentMetadataQuery>(
    GET_ATTACHMENT_METADATA,
    {
      fetchPolicy: 'network-only',
    },
  );
  // reset count when fileUri changes
  useEffect(() => {
    count.current = MAX_ATTEMPTS;
  }, [fileUri]);

  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  }, [onStatusChange]);

  useEffect(() => {
    const scanFileForVirus = async (uri: string) => {
      if (count.current <= 0) {
        return;
      }
      await asyncPoll(
        () => {
          count.current -= 1;
          return fetchFileMetadata({ variables: { blobUrl: uri } });
        },
        ({ data }) => {
          const virusScanStatus =
            data?.viewer?.attachmentMetadata?.virusScanStatus;

          return (
            virusScanStatus === VirusScanStatus.HasVirus ||
            virusScanStatus === VirusScanStatus.Ok
          );
        },
        MAX_ATTEMPTS,
        INTERVAL_MS,
      );
    };

    if (fileUri) {
      scanFileForVirus(fileUri);
    }
  }, [fileUri, fetchFileMetadata]);

  const status = state.data?.viewer?.attachmentMetadata?.virusScanStatus;
  let currentStatus = status || VirusScanStatus.Unknown;
  if (
    count.current <= 0 &&
    (currentStatus === VirusScanStatus.Pending ||
      currentStatus === VirusScanStatus.Unknown)
  ) {
    // allow to pass when all attempts exhausted
    currentStatus = VirusScanStatus.Ok;
  }

  useEffect(() => {
    onStatusChangeRef.current?.(currentStatus, currentMethod);
  }, [currentStatus, currentMethod]);

  return {
    virusScanStatus: currentStatus as VirusScanStatus,
    error: state.error,
    isValidating: state.loading,
  };
}
